// ─────────────────────────────────────────────────────────────────────────
// Payment provider switch — the single source of truth for "who takes the
// money for a NEW purchase".
//
// WHY IT EXISTS: Stripe blocked the account, so Paddle became the live
// processor, and Chargebee is now the third. Nothing is ever deleted — every
// file, route, webhook handler and constant for a retired processor stays in
// the tree and stays correct. Setting this flag back to `stripe` (or `paddle`)
// restores that processor's behaviour exactly, the same discipline
// lib/billing/payments-paused.ts established: restoring is an env change +
// redeploy, NOT a code revert.
//
// THE RULE THAT SHAPES EVERYTHING: this flag governs NEW purchases only.
// Servicing an EXISTING subscription is decided by the user_billing row, via
// providerForSubscription() in lib/payments/refs.ts — never by this flag. A
// subscriber created on Stripe keeps being upgraded, downgraded, paused and
// cancelled through Stripe forever, no matter what this says; the same is true
// of a Paddle or a Chargebee subscriber. That is what makes both the cutover
// and the rollback safe, and it is what lets three processors coexist in one
// deployment rather than one replacing another.
//
// WHERE IT IS READ:
//   • mint routes (subscribe, top-up, trial, card setup) — branch here.
//   • lifecycle routes (change-plan, upgrade, downgrade, cancel, resume,
//     switch-annual, trial/convert) — do NOT branch here. They branch on the
//     row, through apiForRow() in lib/payments/registry.ts. See refs.ts.
//   • client — app/checkout picks the on-page checkout component.
//
// PRECEDENCE at the top of every money route, in this order:
//   1. isPaymentsPaused()        → 503. The emergency brake sits ABOVE the
//                                  provider choice; every processor can have
//                                  an outage.
//   2. paymentProvider()         → dispatch to Stripe, Paddle or Chargebee.
//   3. isCustomCheckoutEnabled() → on-page vs redirect, WITHIN the provider.
//
// The NEXT_PUBLIC_ prefix is deliberate even though the server also reads it:
// the client needs it inlined at build time to pick a checkout component, and a
// second server-only variable would be a second thing to remember to flip —
// and a build where the two disagreed would mint one provider's session into
// the other's UI. Nothing here is a secret.
// ─────────────────────────────────────────────────────────────────────────

export type PaymentProviderId =
  | "stripe"
  | "paddle"
  | "chargebee"
  | "paypal"
  | "whop";

/**
 * Who takes the money for a new purchase.
 *
 * Defaults to `stripe` when unset — and, just as importantly, when set to
 * anything unrecognised. That fail-safe is load-bearing: a typo'd or
 * half-deployed env value must land on the processor whose code path is
 * oldest and best proven, not on an unhandled third state. Merging a new
 * provider therefore changes nothing until the environment says otherwise,
 * exactly like the pause switch.
 */
export function paymentProvider(): PaymentProviderId {
  const value = process.env.NEXT_PUBLIC_PAYMENT_PROVIDER;
  return value === "paddle" ||
    value === "chargebee" ||
    value === "paypal" ||
    value === "whop"
    ? value
    : "stripe";
}

/** Sugar for the common branch. New purchases only — never for servicing. */
export function isPaddle(): boolean {
  return paymentProvider() === "paddle";
}

/** Sugar for the common branch. New purchases only — never for servicing. */
export function isChargebee(): boolean {
  return paymentProvider() === "chargebee";
}

/** Sugar for the common branch. New purchases only — never for servicing. */
export function isPayPal(): boolean {
  return paymentProvider() === "paypal";
}

/** Sugar for the common branch. New purchases only — never for servicing. */
export function isWhop(): boolean {
  return paymentProvider() === "whop";
}

/**
 * Whop environment for the REST base URL. Sandbox unless explicitly production,
 * the same fail-safe as paypalEnvironment(), paddleEnvironment() and
 * chargebeeIsLive().
 *
 * This one carries MORE weight than the others, because Whop is the first
 * provider whose payment objects have NO livemode field at all. Stripe answers
 * `session.livemode`; Paddle and Chargebee can be asked. Whop separates test
 * from real money by BASE URL and account only, so this function is the single
 * definition of `livemode` on every Whop CompletedPurchase — and the trial's
 * one-card-per-account fingerprint guard keys on it. A capture wrongly marked
 * live blacklists a REAL customer's card on the strength of a QA run.
 */
export function whopEnvironment(): "sandbox" | "production" {
  // `||`, NOT `??`, for the reason paypalEnvironment() documents: a blank
  // string is "not configured", and `??` only falls through on null/undefined,
  // so an empty WHOP_ENV would mask a correctly-set NEXT_PUBLIC_WHOP_ENV and
  // silently keep production on sandbox.
  const declared = (process.env.WHOP_ENV || process.env.NEXT_PUBLIC_WHOP_ENV || "")
    .trim()
    .toLowerCase();
  return declared === "production" || declared === "live"
    ? "production"
    : "sandbox";
}

/**
 * PayPal environment for the REST base URL and the client-side SDK.
 *
 * Sandbox unless explicitly production, the same fail-safe as
 * paddleEnvironment() and chargebeeIsLive(): a missing or misspelt value can
 * never point test traffic at live money. `livemode` on every
 * CompletedPurchase is derived from this, and the trial's one-card-per-account
 * fingerprint guard keys on it — a capture wrongly marked live blacklists a
 * REAL customer's card on the strength of a QA run.
 */
export function paypalEnvironment(): "sandbox" | "production" {
  // `||`, NOT `??`. A blank string is "not configured", and `??` only falls
  // through on null/undefined — so with `??` an empty PAYPAL_ENV (a secret that
  // exists but was never filled in) would mask a correctly-set
  // NEXT_PUBLIC_PAYPAL_ENV and silently keep production on sandbox. That is the
  // exact shape of the two-variables-disagreeing bug lib/payments/chargebee/
  // client.ts documents.
  const declared = (
    process.env.PAYPAL_ENV ||
    process.env.NEXT_PUBLIC_PAYPAL_ENV ||
    ""
  )
    .trim()
    .toLowerCase();
  return declared === "production" || declared === "live"
    ? "production"
    : "sandbox";
}

/**
 * Paddle environment for the SDK + client token. Sandbox unless explicitly
 * production, so a missing value can never point test traffic at live money.
 */
export function paddleEnvironment(): "sandbox" | "production" {
  return process.env.NEXT_PUBLIC_PADDLE_ENV === "production"
    ? "production"
    : "sandbox";
}
